import type { CardFontSchemaType, CardSchemaType } from '@vedalib/editor/lib/brand'
import { IconsAlignEnum } from '@vedalib/editor/lib/brand'
import type { CardElementValue } from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import type { RichTextValue } from '@vedalib/rich-text'
import cn from 'classnames'
import lodash from 'lodash'
import * as R from 'ramda'
import type { RefObject } from 'react'
import { useCallback } from 'react'

import { parseStyleValue } from 'components/Editor/cource/controls/CourseSidebar/utils'
import RichText from 'components/form/RichText/RichText'
import type { ElementFontCss, ElementStyleCss } from 'services/Branding/types'
import { PreviewMode } from 'services/Store/Project/enums'
import type { IBlockMode } from 'services/Store/Project/types'
import { t } from 'services/Translation'
import { useUrlByUsage } from 'utils/files'
import { testProps } from 'utils/test/qaData'

import { getObjectFitDivMap } from '../../utils/getObjectFitDivMap'
import * as s from './CardSide.module.scss'
import Icon from './assets/cardIcon.svg?react'
import { CardSidesEnum } from './enum'
import IconButtonStyled from './styled/IconStyled'
import { getPositionIcon, STYLES_ICON_SIDE_KEYS, STYLES_SIDE_KEYS } from './utils'

export const DEFAULT_TEXT = t('input.placeholder.addText')
const DEF_INDENT_FROM_TEXT = 16

interface ICardSideProps {
  side: CardSidesEnum
  styles: ElementStyleCss<CardSchemaType>
  value: CardElementValue
  active: boolean
  mode: IBlockMode
  onChange?: (value: CardElementValue) => void
  onClick?: () => void
  onMouseEnter?: () => void
  waiting?: boolean
  font: ElementFontCss<CardFontSchemaType>
  offShadow?: boolean
  rtProps: {
    isActiveElement: boolean
    activeIndex: string
    onLabelSelect: (newIndex: string) => void
    isFill: boolean
    cursorPosition: 'start' | 'end'
  }
  bookRef?: RefObject<HTMLDivElement | null>
  titleId?: string
}

const CardSide = ({
  side,
  styles,
  value,
  active,
  mode,
  onChange,
  onClick,
  waiting,
  font,
  onMouseEnter,
  offShadow,
  rtProps,
  bookRef,
  titleId,
}: ICardSideProps) => {
  const sideValues = value[side]
  const stylesKey = STYLES_SIDE_KEYS[side]
  const stylesIconKey = STYLES_ICON_SIDE_KEYS[side]
  const iconAlign = styles[stylesIconKey].iconsAlign as IconsAlignEnum
  const iconBg = styles[stylesIconKey].backgroundImage
  const borderRadius = parseInt(String(styles.card.borderRadius))
  const borderWidth = parseInt(String(styles.border.borderWidth))
  const borderRadiusContent = borderRadius > borderWidth ? borderRadius - borderWidth : 0
  const maskStyles = { backgroundColor: sideValues.overlay || undefined }
  const isTopIconPosition =
    iconAlign === IconsAlignEnum.leftUp || iconAlign === IconsAlignEnum.rightUp
  const isBottomIconPosition =
    iconAlign === IconsAlignEnum.leftDown || iconAlign === IconsAlignEnum.rightDown
  const iconPositionStyle = getPositionIcon(iconAlign)
  const iconStyle = {
    ...iconPositionStyle,
    backgroundImage: iconBg,
  }
  const cardName = `card.${side}`
  const { isActiveElement, activeIndex, isFill, onLabelSelect } = rtProps || {}

  const url = useUrlByUsage(sideValues.image, FileUsageTypeEnum.image)
  const text = sideValues.label || (url ? '' : DEFAULT_TEXT)
  const rootStyles = {
    ...(!offShadow && styles.effects),
    ...styles.border,
    backgroundImage: url ? `url(${url})` : undefined,
    ...lodash.omit(styles[stylesKey], ['alignItems', 'justifyContent', 'backgroundImage']),
    borderRadius: isNaN(borderRadius) ? styles.card.borderRadius : borderRadiusContent,
    backgroundSize: getObjectFitDivMap(sideValues.image?.objectFit),
    paddingTop:
      (isTopIconPosition ? parseStyleValue(styles.icon.height) : 0) + DEF_INDENT_FROM_TEXT,
    paddingBottom:
      (isBottomIconPosition ? parseStyleValue(styles.icon.height) : 0) + DEF_INDENT_FROM_TEXT,
  }

  const onChangeLabel = useCallback(
    (labelValue: RichTextValue) => onChange?.(R.assocPath([side, 'label'], labelValue, value)),
    [onChange, side, value],
  )

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const book = bookRef?.current
      if (!book) {
        return
      }
      const indices = side === CardSidesEnum.FRONT ? [0, 1] : [2, 3]
      indices.forEach((i) => {
        const container = book.children[i]?.firstElementChild?.children[1]
        if (container && container !== e.currentTarget) {
          container.scrollTop = e.currentTarget.scrollTop
        }
      })
    },
    [bookRef, side],
  )

  return (
    <div
      aria-hidden={!active}
      aria-label={sideValues.image?.accessibility}
      className={cn(s.root, s[side], [s[styles.animation.cardFlip]], {
        [s.active]: active,
        [s.pdf]: mode.previewMode === PreviewMode.pdf,
      })}
      style={rootStyles}
    >
      <div className={s.mask} style={maskStyles} />
      <div
        className={s.textContainer}
        onScroll={handleScroll}
        style={{ justifyContent: styles[stylesKey].alignItems }}
      >
        <div className={s.textInner} id={titleId} onSelect={() => onLabelSelect(cardName)}>
          <RichText
            active={isActiveElement && activeIndex === cardName && active}
            disabled={!isFill}
            isPreview={mode?.previewMode !== PreviewMode.editor}
            key={cardName}
            name={cardName}
            onChange={onChangeLabel}
            styles={font}
            value={text}
            waiting={waiting}
          />
        </div>
      </div>
      <IconButtonStyled
        $hover={isFill}
        $isBack={side === CardSidesEnum.BACK}
        $styles={styles}
        as='div'
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        style={iconStyle}
        aria-hidden
        {...testProps({ el: 'cardIcon', side })}
      >
        {!iconBg && <Icon />}
      </IconButtonStyled>
    </div>
  )
}

export default CardSide
